/**
 * Catálogo de códigos de erro (`docs/api/03-endpoints-draft.md §14`).
 *
 * O código é o contrato estável; a mensagem é texto em pt-BR que pode mudar sem
 * quebrar cliente (RNF-110, RNF-115). Nenhuma mensagem deste arquivo revela
 * existência de e-mail, de recurso fora do escopo ou motivo de negação —
 * RN-01, RNF-24.
 *
 * Cobre segurança, validação, paginação, genéricos e o domínio de lead
 * (atendimento, transferência, agendamento e conversão). A tabela §14 do
 * rascunho é a referência; onde este arquivo vai além dela, o motivo está no
 * comentário da linha.
 */

export const CODIGOS_ERRO = {
  // --- 401 — autenticação --------------------------------------------------
  /** Login incorreto, usuário inativo ou convite não aceito. Mensagem única (RN-01/RN-02). */
  CREDENCIAIS_INVALIDAS: 'CREDENCIAIS_INVALIDAS',
  /** JWT ausente, malformado, vencido ou emitido antes da última troca de senha. */
  SESSAO_EXPIRADA: 'SESSAO_EXPIRADA',
  /** Refresh já rotacionado — a família inteira foi revogada (RNF-12). */
  REFRESH_REUSADO: 'REFRESH_REUSADO',
  /**
   * [+] Webhook de integração com assinatura ausente, malformada ou divergente
   * (RNF-40 / §12, item 1 do contrato do webhook). 401 e não 403: quem chama não
   * é um usuário sem permissão, é um chamador que não provou de onde vem.
   */
  ASSINATURA_INTEGRACAO_INVALIDA: 'ASSINATURA_INTEGRACAO_INVALIDA',

  // --- 403 — autorização ---------------------------------------------------
  SEM_PERMISSAO: 'SEM_PERMISSAO',
  /** Double-submit de CSRF ausente ou divergente (RNF-37). */
  CSRF_INVALIDO: 'CSRF_INVALIDO',
  /** `deve_trocar_senha = true`: só as rotas de troca respondem (RN-06). */
  TROCA_SENHA_OBRIGATORIA: 'TROCA_SENHA_OBRIGATORIA',

  // --- 404 — inexistente ou fora do escopo (RNF-24) ------------------------
  RECURSO_NAO_ENCONTRADO: 'RECURSO_NAO_ENCONTRADO',

  // --- 409 — conflito de estado -------------------------------------------
  /** RN-10: o cliente já tem atendimento em andamento. O corpo traz o lead. */
  CLIENTE_COM_ATENDIMENTO_EM_ANDAMENTO: 'CLIENTE_COM_ATENDIMENTO_EM_ANDAMENTO',
  /** [+] `formularios_meta.formulario_externo_id` é único: já existe cadastro. */
  FORMULARIO_META_DUPLICADO: 'FORMULARIO_META_DUPLICADO',
  /**
   * RN-42 — título repetido entre registros **ativos** do mesmo catálogo,
   * ignorando caixa e acento. Vale para unidades e equipes também: as três famílias
   * compartilham o índice único parcial.
   */
  CATALOGO_TITULO_DUPLICADO: 'CATALOGO_TITULO_DUPLICADO',
  /**
   * [+] RN-07A / FL-14: desativação de usuário com carteira aberta pedida **sem**
   * `destino`. O corpo traz a contagem, e a tela usa este `409` como passo do
   * fluxo — não como erro.
   */
  USUARIO_POSSUI_LEADS_EM_ABERTO: 'USUARIO_POSSUI_LEADS_EM_ABERTO',
  /**
   * [+] Desativar unidade ou equipe que ainda tem lead não terminal, ou equipe
   * ativa (no caso da unidade). Registro em uso não desaparece de baixo da
   * operação: o `409` diz o que precisa ser esvaziado primeiro.
   */
  ORGANIZACAO_EM_USO: 'ORGANIZACAO_EM_USO',

  // --- 422 — domínio do lead (RN-20 a RN-27) -------------------------------
  /** RN-20. `detalhes` traz os destinos válidos a partir do status atual. */
  LEAD_TRANSICAO_INVALIDA: 'LEAD_TRANSICAO_INVALIDA',
  /** RN-23. `detalhes` traz um item por campo faltante. */
  LEAD_CAMPO_OBRIGATORIO_POR_STATUS: 'LEAD_CAMPO_OBRIGATORIO_POR_STATUS',
  /** RN-26A (⚠️ pode mudar). */
  LEAD_TERMINAL_NAO_TRANSFERIVEL: 'LEAD_TERMINAL_NAO_TRANSFERIVEL',
  /** RN-20, última linha: sair de terminal é só por `/reabrir`, e só Gestor+. */
  LEAD_NAO_ESTA_TERMINAL: 'LEAD_NAO_ESTA_TERMINAL',
  /** RN-11, validado no servidor mesmo com contador na tela. */
  OBSERVACAO_MUITO_CURTA: 'OBSERVACAO_MUITO_CURTA',
  /** I1: responsável precisa ser usuário ativo e membro da equipe do lead. */
  RESPONSAVEL_INVALIDO: 'RESPONSAVEL_INVALIDO',
  /** I2: `leads.equipe_id` precisa pertencer a `leads.unidade_id`. */
  EQUIPE_FORA_DA_UNIDADE: 'EQUIPE_FORA_DA_UNIDADE',
  /** RN-11A: catálogo desativado não pode ser escolhido em registro novo. */
  MOTIVO_INATIVO: 'MOTIVO_INATIVO',
  /**
   * [+] Interação em lead sem responsável. Defesa em profundidade: só o ATENDENTE o
   * dispara, e o escopo dele já devolve `404` antes de chegar à checagem.
   */
  LEAD_SEM_RESPONSAVEL: 'LEAD_SEM_RESPONSAVEL',
  /**
   * [+] RN-26B: lote acima do limite síncrono. O rascunho prevê `202` + job;
   * enquanto a fila não existir, recusar é melhor que transferir metade.
   */
  LOTE_ACIMA_DO_LIMITE_SINCRONO: 'LOTE_ACIMA_DO_LIMITE_SINCRONO',

  // --- 422 — agendamento (FL-09) ------------------------------------------
  /** RN-24A: `iniciaEm` precisa ser futuro no momento da criação. */
  AGENDAMENTO_DATA_PASSADA: 'AGENDAMENTO_DATA_PASSADA',
  /** [+] Concluir/cancelar/reagendar exige compromisso ainda `AGENDADO`. */
  AGENDAMENTO_NAO_ESTA_ATIVO: 'AGENDAMENTO_NAO_ESTA_ATIVO',

  // --- 422 — integrações (§12) ---------------------------------------------
  /**
   * [+] Evento já `PROCESSADO`. Reprocessar criaria o segundo lead que o RN-61
   * existe para impedir — e a timeline do primeiro não pode ser desfeita (RN-25).
   */
  EVENTO_NAO_REPROCESSAVEL: 'EVENTO_NAO_REPROCESSAVEL',
  /**
   * [+] Operação que fala com a Meta pedida sem credencial configurada
   * (`META_ACCESS_TOKEN` ausente em `META_PROVEDOR=http`). Falha explícita em vez
   * de sincronização que "roda" e não traz nada.
   */
  INTEGRACAO_SEM_CREDENCIAL: 'INTEGRACAO_SEM_CREDENCIAL',
  /** [+] A Meta (ou o portal) recusou ou não respondeu. Detalhe fica no log. */
  INTEGRACAO_INDISPONIVEL: 'INTEGRACAO_INDISPONIVEL',

  // --- Meta Ads (agente 24) ------------------------------------------------
  /**
   * [+] Escrita na Meta pedida com `META_ADS_ESCRITA_ATIVA=false`.
   *
   * 409 e não 403: quem pediu **tem** a permissão; o que recusa é o estado do ambiente.
   * Um 403 mandaria o gestor pedir acesso que ele já tem.
   */
  META_ADS_ESCRITA_DESATIVADA: 'META_ADS_ESCRITA_DESATIVADA',
  /** [+] Conta de anúncio cadastrada e marcada como inativa no CRM. */
  META_ADS_CONTA_INATIVA: 'META_ADS_CONTA_INATIVA',
  /** [+] `act_id` já cadastrado — a conta é única por id externo. */
  META_ADS_CONTA_DUPLICADA: 'META_ADS_CONTA_DUPLICADA',
  /**
   * [+] Alvo (campanha, conjunto, anúncio) não sincronizado.
   *
   * 404 próprio, e não `RECURSO_NAO_ENCONTRADO`: a mensagem precisa dizer *o que fazer*
   * — rodar a sincronização de estrutura —, e uma mensagem genérica faria o gestor
   * concluir que o anúncio não existe mais na Meta.
   */
  META_ADS_ALVO_NAO_SINCRONIZADO: 'META_ADS_ALVO_NAO_SINCRONIZADO',
  /** [+] Ação do catálogo que ainda não é executada automaticamente (§16, Fase 3). */
  META_ADS_ACAO_NAO_IMPLEMENTADA: 'META_ADS_ACAO_NAO_IMPLEMENTADA',
  /** [+] Recomendação já aplicada, descartada ou expirada. */
  META_ADS_RECOMENDACAO_RESOLVIDA: 'META_ADS_RECOMENDACAO_RESOLVIDA',
  /**
   * [+] Recurso de IA pedido com `IA_PROVEDOR=desligado`.
   *
   * 422 e não 500: não é falha, é configuração — e o restante do Meta Ads continua
   * funcionando, o que a mensagem diz explicitamente.
   */
  IA_DESLIGADA: 'IA_DESLIGADA',

  // --- 422 — validação -----------------------------------------------------
  DADOS_INVALIDOS: 'DADOS_INVALIDOS',
  SENHA_FRACA: 'SENHA_FRACA',
  SENHA_IGUAL_ANTERIOR: 'SENHA_IGUAL_ANTERIOR',
  SENHA_ATUAL_INCORRETA: 'SENHA_ATUAL_INCORRETA',
  /** Inexistente, expirado, já usado ou invalidado — sem distinguir qual (RN-04). */
  TOKEN_SENHA_INVALIDO: 'TOKEN_SENHA_INVALIDO',
  ULTIMO_ADMINISTRADOR: 'ULTIMO_ADMINISTRADOR',
  /**
   * [+] Equipe cujo `unidade_id` não está entre as unidades do usuário — a lotação
   * (`usuarios.unidade_id`) mais o escopo (`usuarios_unidades`).
   *
   * Invariante do vínculo, conferida no servidor em toda escrita que pode violá-la
   * (criar usuário, `PUT /equipes`, `PUT /unidades`, troca de lotação). O seletor da
   * TELA-10/11 aplica a mesma regra, mas UI não é barreira: sem esta conferência,
   * `curl` daria a um supervisor equipe de unidade onde ele não tem vínculo, e o
   * escopo dele passaria a alcançar leads daquela unidade.
   */
  EQUIPE_FORA_DAS_UNIDADES: 'EQUIPE_FORA_DAS_UNIDADES',
  /**
   * [+] C3 (`03-distribution-decision.md §3.3`): tentativa de configurar
   * distribuição automática por rodízio. Não é "valor inválido" — é uma regra de
   * negócio que mudou, e a mensagem precisa dizer isso.
   */
  DISTRIBUICAO_AUTOMATICA_DESATIVADA: 'DISTRIBUICAO_AUTOMATICA_DESATIVADA',
  /**
   * [+] I11 (C3): `equipes.responsavel_id` precisa ser usuário **ativo** e **membro
   * da própria equipe**. Separado de `RESPONSAVEL_INVALIDO` (I1, que é sobre o
   * responsável de um *lead*) para a tela poder destacar o campo certo.
   */
  RESPONSAVEL_EQUIPE_INVALIDO: 'RESPONSAVEL_EQUIPE_INVALIDO',
  /**
   * [+] RN-70: ninguém altera o próprio papel, equipes, unidades ou status. O
   * caminho da própria conta é `/auth/alterar-senha` e o perfil.
   */
  ACAO_SOBRE_SI_MESMO: 'ACAO_SOBRE_SI_MESMO',
  FILTRO_DESCONHECIDO: 'FILTRO_DESCONHECIDO',
  ORDENACAO_NAO_PERMITIDA: 'ORDENACAO_NAO_PERMITIDA',
  LIMITE_PAGINACAO_EXCEDIDO: 'LIMITE_PAGINACAO_EXCEDIDO',
  /** [+] Cursor ilegível ou de versão desconhecida — entrada do cliente, não bug. */
  CURSOR_INVALIDO: 'CURSOR_INVALIDO',
  /** [+] Janela do calendário acima de 31 dias (§7) ou período invertido. */
  PERIODO_INVALIDO: 'PERIODO_INVALIDO',

  // --- 429 -----------------------------------------------------------------
  LIMITE_REQUISICOES: 'LIMITE_REQUISICOES',
  /** Bloqueio por tentativas falhas de login (RNF-15). Acompanha `Retry-After`. */
  CONTA_BLOQUEADA: 'CONTA_BLOQUEADA',

  // --- 400 / 500 -----------------------------------------------------------
  REQUISICAO_MALFORMADA: 'REQUISICAO_MALFORMADA',
  ERRO_INTERNO: 'ERRO_INTERNO',
} as const

export type CodigoErro = (typeof CODIGOS_ERRO)[keyof typeof CODIGOS_ERRO]

/** Mapa código → HTTP. Única fonte: nenhum caso de uso escolhe status na mão. */
export const HTTP_POR_CODIGO: Readonly<Record<CodigoErro, number>> = {
  CREDENCIAIS_INVALIDAS: 401,
  SESSAO_EXPIRADA: 401,
  REFRESH_REUSADO: 401,
  ASSINATURA_INTEGRACAO_INVALIDA: 401,

  SEM_PERMISSAO: 403,
  CSRF_INVALIDO: 403,
  TROCA_SENHA_OBRIGATORIA: 403,

  RECURSO_NAO_ENCONTRADO: 404,

  CLIENTE_COM_ATENDIMENTO_EM_ANDAMENTO: 409,
  FORMULARIO_META_DUPLICADO: 409,
  CATALOGO_TITULO_DUPLICADO: 409,
  USUARIO_POSSUI_LEADS_EM_ABERTO: 409,
  ORGANIZACAO_EM_USO: 409,

  EVENTO_NAO_REPROCESSAVEL: 422,
  INTEGRACAO_SEM_CREDENCIAL: 422,
  INTEGRACAO_INDISPONIVEL: 422,

  META_ADS_CONTA_DUPLICADA: 409,
  META_ADS_ESCRITA_DESATIVADA: 409,
  META_ADS_CONTA_INATIVA: 409,
  META_ADS_ALVO_NAO_SINCRONIZADO: 404,
  META_ADS_ACAO_NAO_IMPLEMENTADA: 422,
  META_ADS_RECOMENDACAO_RESOLVIDA: 422,
  IA_DESLIGADA: 422,

  LEAD_TRANSICAO_INVALIDA: 422,
  LEAD_CAMPO_OBRIGATORIO_POR_STATUS: 422,
  LEAD_TERMINAL_NAO_TRANSFERIVEL: 422,
  LEAD_NAO_ESTA_TERMINAL: 422,
  OBSERVACAO_MUITO_CURTA: 422,
  RESPONSAVEL_INVALIDO: 422,
  EQUIPE_FORA_DA_UNIDADE: 422,
  MOTIVO_INATIVO: 422,
  LEAD_SEM_RESPONSAVEL: 422,
  LOTE_ACIMA_DO_LIMITE_SINCRONO: 422,

  AGENDAMENTO_DATA_PASSADA: 422,
  AGENDAMENTO_NAO_ESTA_ATIVO: 422,

  DADOS_INVALIDOS: 422,
  SENHA_FRACA: 422,
  SENHA_IGUAL_ANTERIOR: 422,
  SENHA_ATUAL_INCORRETA: 422,
  TOKEN_SENHA_INVALIDO: 422,
  ULTIMO_ADMINISTRADOR: 422,
  EQUIPE_FORA_DAS_UNIDADES: 422,
  DISTRIBUICAO_AUTOMATICA_DESATIVADA: 422,
  RESPONSAVEL_EQUIPE_INVALIDO: 422,
  // 403 e não 422: o pedido é bem formado, o que falta é permissão para exercê-lo
  // sobre si mesmo (RN-70).
  ACAO_SOBRE_SI_MESMO: 403,
  FILTRO_DESCONHECIDO: 422,
  ORDENACAO_NAO_PERMITIDA: 422,
  LIMITE_PAGINACAO_EXCEDIDO: 422,
  CURSOR_INVALIDO: 422,
  PERIODO_INVALIDO: 422,

  LIMITE_REQUISICOES: 429,
  CONTA_BLOQUEADA: 429,

  REQUISICAO_MALFORMADA: 400,
  ERRO_INTERNO: 500,
}

/**
 * Mensagem padrão em pt-BR. Específica e acionável (RNF-115), sem revelar nada
 * além do necessário. O caso de uso pode sobrescrever quando tiver contexto —
 * desde que a substituição não vaze informação.
 */
export const MENSAGEM_POR_CODIGO: Readonly<Record<CodigoErro, string>> = {
  CREDENCIAIS_INVALIDAS: 'E-mail ou senha incorretos.',
  SESSAO_EXPIRADA: 'Sua sessão expirou. Entre novamente.',
  REFRESH_REUSADO:
    'Sua sessão foi encerrada por segurança. Entre novamente para continuar.',
  // Genérica de propósito: a resposta do webhook não diz se o problema foi o
  // cabeçalho ausente, o formato ou o segredo errado (§4.4 da arquitetura).
  ASSINATURA_INTEGRACAO_INVALIDA: 'Não foi possível validar a origem da requisição.',

  SEM_PERMISSAO: 'Você não tem permissão para esta ação.',
  CSRF_INVALIDO: 'Não foi possível validar a origem da requisição. Recarregue a página.',
  TROCA_SENHA_OBRIGATORIA: 'Defina uma nova senha para continuar usando o sistema.',

  RECURSO_NAO_ENCONTRADO: 'Registro não encontrado.',

  CLIENTE_COM_ATENDIMENTO_EM_ANDAMENTO:
    'Este cliente já possui atendimento em andamento. Abra o atendimento existente ou confirme a criação de um novo.',

  FORMULARIO_META_DUPLICADO:
    'Já existe um formulário cadastrado com este identificador da Meta.',
  CATALOGO_TITULO_DUPLICADO:
    'Já existe um registro ativo com este título. Escolha outro título ou reative o existente.',
  USUARIO_POSSUI_LEADS_EM_ABERTO:
    'Este usuário tem leads em aberto. Escolha o destino da carteira antes de desativar.',
  ORGANIZACAO_EM_USO:
    'Este registro está em uso e não pode ser desativado agora. Transfira o que está vinculado a ele primeiro.',
  EVENTO_NAO_REPROCESSAVEL:
    'Este evento já foi processado e gerou lead. Reprocessar criaria um atendimento duplicado.',
  INTEGRACAO_SEM_CREDENCIAL:
    'A integração com a Meta não está configurada neste ambiente. Configure as credenciais antes de sincronizar.',
  INTEGRACAO_INDISPONIVEL:
    'Não foi possível falar com a Meta agora. O evento continua na fila e será tentado novamente.',

  META_ADS_CONTA_DUPLICADA:
    'Esta conta de anúncio já está cadastrada. Edite o cadastro existente em vez de criar outro.',
  META_ADS_ESCRITA_DESATIVADA:
    'A execução de ações na Meta está desativada neste ambiente. A recomendação continua ' +
    'registrada e pode ser aplicada manualmente no Gerenciador de Anúncios.',
  META_ADS_CONTA_INATIVA:
    'Esta conta de anúncio está inativa no CRM. Reative o cadastro antes de executar ações.',
  META_ADS_ALVO_NAO_SINCRONIZADO:
    'Este anúncio, conjunto ou campanha ainda não foi sincronizado. Rode a sincronização ' +
    'de estrutura e tente de novo.',
  META_ADS_ACAO_NAO_IMPLEMENTADA:
    'Esta ação ainda não é executada automaticamente. Variações de criativo passam pela ' +
    'tela de aprovação antes de ir para a Meta.',
  META_ADS_RECOMENDACAO_RESOLVIDA:
    'Esta recomendação já foi aplicada, descartada ou expirou.',
  IA_DESLIGADA:
    'A análise por IA está desativada neste ambiente. Métricas, score, diagnóstico e ' +
    'recomendações continuam disponíveis.',

  LEAD_TRANSICAO_INVALIDA: 'Esta mudança de situação não é permitida para o lead.',
  LEAD_CAMPO_OBRIGATORIO_POR_STATUS:
    'A situação escolhida exige informações adicionais. Revise os campos destacados.',
  LEAD_TERMINAL_NAO_TRANSFERIVEL: 'Lead já encerrado não pode ser transferido.',
  LEAD_NAO_ESTA_TERMINAL: 'Este lead não está encerrado, portanto não precisa ser reaberto.',
  OBSERVACAO_MUITO_CURTA: 'Insira ao menos 20 caracteres na observação do atendimento.',
  RESPONSAVEL_INVALIDO:
    'O responsável precisa ser um usuário ativo e membro da equipe do lead.',
  EQUIPE_FORA_DA_UNIDADE: 'A equipe escolhida não pertence à unidade do lead.',
  MOTIVO_INATIVO: 'O motivo escolhido está desativado. Selecione um motivo ativo.',
  LEAD_SEM_RESPONSAVEL:
    'Este lead está sem responsável. Assuma o atendimento antes de registrar a interação.',
  LOTE_ACIMA_DO_LIMITE_SINCRONO:
    'Seleção acima do limite processado de uma vez. Divida a operação em lotes menores.',

  AGENDAMENTO_DATA_PASSADA: 'A data do agendamento precisa ser no futuro.',
  AGENDAMENTO_NAO_ESTA_ATIVO: 'Este agendamento já foi encerrado.',

  DADOS_INVALIDOS: 'Revise os campos destacados.',
  SENHA_FRACA: 'A senha não atende à política de segurança.',
  SENHA_IGUAL_ANTERIOR: 'A nova senha precisa ser diferente da anterior.',
  SENHA_ATUAL_INCORRETA: 'A senha atual está incorreta.',
  TOKEN_SENHA_INVALIDO:
    'Este link não é mais válido. Solicite uma nova recuperação de senha.',
  ULTIMO_ADMINISTRADOR:
    'É preciso manter ao menos um administrador ativo no sistema.',
  EQUIPE_FORA_DAS_UNIDADES:
    'A equipe precisa pertencer a uma das unidades vinculadas ao usuário. Inclua a unidade da equipe no escopo do usuário antes de vinculá-la.',
  DISTRIBUICAO_AUTOMATICA_DESATIVADA:
    'A distribuição automática por rodízio foi desativada. O lead novo vai para o responsável da equipe, que faz o pré-atendimento e atribui o corretor de plantão.',
  RESPONSAVEL_EQUIPE_INVALIDO:
    'O responsável da equipe precisa ser um usuário ativo e membro desta equipe.',
  ACAO_SOBRE_SI_MESMO:
    'Você não pode alterar o seu próprio papel, equipes, unidades ou situação de acesso.',
  FILTRO_DESCONHECIDO: 'Filtro não reconhecido para esta listagem.',
  ORDENACAO_NAO_PERMITIDA: 'Não é possível ordenar por este campo.',
  LIMITE_PAGINACAO_EXCEDIDO: 'O limite de itens por página foi excedido.',
  CURSOR_INVALIDO: 'A paginação expirou. Recarregue a lista.',
  PERIODO_INVALIDO: 'Período inválido. Confira as datas informadas.',

  LIMITE_REQUISICOES: 'Muitas tentativas. Aguarde um momento e tente novamente.',
  CONTA_BLOQUEADA:
    'Acesso bloqueado temporariamente por tentativas incorretas. Tente novamente mais tarde.',

  REQUISICAO_MALFORMADA: 'Requisição inválida.',
  ERRO_INTERNO: 'Não foi possível concluir a operação. Tente novamente em instantes.',
}

/** Formato de erro de `01-architecture.md §9`. Idêntico em toda resposta. */
export interface DetalheErro {
  readonly campo: string
  readonly codigo: string
  readonly mensagem: string
}

export interface RespostaErro {
  readonly erro: {
    readonly codigo: CodigoErro
    readonly mensagem: string
    readonly detalhes?: ReadonlyArray<DetalheErro>
    readonly requestId: string
  }
}
